import React from 'react';
import PropTypes from 'prop-types';
import VolumeMeter from './VolumeMeter';

import constants from '../../../../qti/Constants';
import { t } from '../../../../services/i18nService';

import styles from './audioRecordInteraction.scss';
import Prompt from '../Prompt';
import utilService from '../../../../services/UtilService';
import marketplacePropertiesService from '../../../../services/MarketplacePropertiesService';
import audioRecordService from '../../../../services/AudioRecordService';

const EXAM_CONTEXT = constants.componentContexts.EXAM;
const PREVIEW_CONTEXT = constants.componentContexts.PREVIEW;

let mediaRecorder;
let recordedFileFormat = null;
const recordedChunks = {};

const STATE_RECORDING_STARTED = 'recording_started';
const STATE_RECORDING_UPLOADING = 'recording_uploading';
const STATE_RECORDING_SAVED = 'recording_saved';

class AudioRecordInteraction extends React.Component {
    static propTypes = {
        callback: PropTypes.func.isRequired,
        context: PropTypes.string.isRequired, // The context in which the question is displayed. Eg: 'exam' or 'preview'.
        handleInteractionReady: PropTypes.func.isRequired,
        isCurrent: PropTypes.bool, // eslint-disable-line react/no-unused-prop-types
        model: PropTypes.object.isRequired, // model taken from responseModels related to this interaction
        questionModel: PropTypes.object.isRequired, // model of the question/assessmentItem holding this interaction.
        direction: PropTypes.string,
        services: PropTypes.object.isRequired,
        userResponse: PropTypes.array.isRequired,
    };

    constructor(props) {
        super(props);
        this.state = {
            recordingState: undefined,
            stream: undefined,
            recordTime: '',
        };
        this.isUnmounting = false;
        this.isRecordStartPending = false;
        this.autoStartFailed = false;
    }

    componentDidMount() {
        setTimeout(() => {
            this.props.handleInteractionReady(true);
        }, 50);
        if (this.shouldAutoStartRecording()) {
            this.startRecord();
        }
    }

    componentDidUpdate(prevProps) {
        if (!prevProps.isCurrent && this.props.isCurrent && this.shouldAutoStartRecording()) {
            this.startRecord();
        }
        if (this.shouldAutoStopRecordingOnQuestionChange(prevProps)) {
            this.autoStopRecording();
        }
    }

    componentWillUnmount() {
        this.isUnmounting = true;
        if (marketplacePropertiesService.isRecordingDurationCounterEnabled()) {
            this.stopTimer();
        }
        if (marketplacePropertiesService.isAutoAudioRecordingEnabled()) {
            this.autoStopRecording();
        }
    }

    startTimer() {
        const startTime = Date.now();
        this.timerIntervalId = setInterval(() => {
            const ms = Date.now() - startTime;
            this.setState({ recordTime: utilService.getReadableDuration(ms, true) });
        }, 1000);
    }

    shouldAutoStopRecordingOnQuestionChange(prevProps) {
        return prevProps.isCurrent &&
            !this.props.isCurrent &&
            prevProps.context === EXAM_CONTEXT &&
            marketplacePropertiesService.isAutoAudioRecordingEnabled();
    }

    stopTimer() {
        clearInterval(this.timerIntervalId);
        if (!this.isUnmounting) {
            this.setState({ recordTime: '' });
        }
    }

    createAndStartRecorder(stream, options) {
        const {
            context,
            questionModel: { contentRevisionId },
        } = this.props;

        recordedChunks[contentRevisionId] = [];

        mediaRecorder = new MediaRecorder(stream, options);
        mediaRecorder.addEventListener('dataavailable', ({ data }) => {
            if (data.size > 0) {
                recordedChunks[contentRevisionId].push(data);
                recordedFileFormat = data.type;
            }
        });

        mediaRecorder.addEventListener('stop', () => {
            if (context === EXAM_CONTEXT) {
                if (!this.isUnmounting) {
                    this.setState({
                        recordingState: STATE_RECORDING_UPLOADING,
                    });
                }
                this.uploadRecording();
            } else if (!this.isUnmounting) {
                this.setState({
                    recordingState: STATE_RECORDING_SAVED,
                });
            }
        });

        mediaRecorder.start();
    }

    startRecord = () => {
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
            console.error('Audio recording is not possible: mediaDevices are not found.');
            return;
        }

        if (typeof MediaRecorder === 'undefined' || !MediaRecorder.isTypeSupported) {
            console.error('Audio recording is not possible: MediaRecorder is not supported.');
            return;
        }

        this.isRecordStartPending = true;
        this.setState({
            recordingState: STATE_RECORDING_STARTED,
            stream: undefined,
        });

        const config = {
            audio: true,
            video: false,
        };

        navigator.mediaDevices.getUserMedia(config)
            .then((stream) => {
                if (!this.isRecordStartPending) {
                    // Recording was cancelled (stopRecord called before getUserMedia resolved).
                    // Release the acquired microphone tracks to avoid leaving it open.
                    stream.getTracks().forEach(track => track.stop());
                    if (!this.isUnmounting) {
                        this.setState({ recordingState: undefined, stream: undefined });
                    }
                    return;
                }
                this.isRecordStartPending = false;

                if (marketplacePropertiesService.isRecordingDurationCounterEnabled()) {
                    this.startTimer();
                }

                const options = {
                    mimeType: audioRecordService.getSuitableMimeType(),
                };

                this.setState({
                    stream,
                });

                this.createAndStartRecorder(stream, options);
            })
            .catch((error) => {
                this.isRecordStartPending = false;
                this.autoStartFailed = true;
                console.error('Error when recording', error);
                if (!this.isUnmounting) {
                    this.setState({ recordingState: undefined, stream: undefined });
                }
            });
    };

    stopRecord = () => {
        this.isRecordStartPending = false;
        if (mediaRecorder) {
            mediaRecorder.stop();
            // Otherwise Safari will produce a recording with ever-growing echo.
            mediaRecorder.stream.getTracks()[0].stop();
        }
    };

    handleFileUploaded = (file, payload) => {
        const { identifier } = this.props.model;
        this.props.callback(identifier, [payload.contentItemId]);
        if (!this.isUnmounting) {
            this.setState({
                recordingState: STATE_RECORDING_SAVED,
            });
        }
    };

    handleCallback = (type, file, payload, id) => {
        console.log('Callback', {
            type,
            file,
            payload,
            id,
        });

        if (type === 'progress') {
            // this.handleProgress(payload);
        } else if (type === 'complete') {
            // this.setState({ uploadedFileS3Name: payload });
        } else if (type === 'sent-upload-success') {
            this.handleFileUploaded(file, payload);
        } else if (type === 'cancelled') {
            // this.handleCancelled(payload);
        } else if (type === 'error') {
            // this.handleError(payload);
        }
    };

    getEffectiveRecordedAudioType = () => {
        return recordedFileFormat;
    };

    uploadRecording = () => {
        const { questionModel: { contentRevisionId } } = this.props;
        if (recordedChunks[contentRevisionId] && recordedChunks[contentRevisionId].length > 0) {
            const blob = new Blob(recordedChunks[contentRevisionId], {
                type: this.getEffectiveRecordedAudioType(),
            });
            blob.name = 'recording';
            this.uploadController = this.props.services.uploadService.upload(
                blob,
                this.props.questionModel.contentItemId,
                this.props.questionModel.contentRevisionId,
                (type, file, payload, id) => {
                    this.handleCallback(type, file, payload, id);
                }
            );
        }
    };

    getAudioUrl = () => {
        const { userResponse, questionModel: { contentRevisionId } } = this.props;
        // TODO: Being able to listen to student audio when he was using Chrome/FF, but I am using Safari.
        if (userResponse && userResponse.length === 1) {
            return `/file/ci/${userResponse[0]}/recording`;
        } else if (recordedChunks[contentRevisionId] && recordedChunks[contentRevisionId].length > 0) {
            const blob = new Blob(recordedChunks[contentRevisionId], {
                type: this.getEffectiveRecordedAudioType(),
            });
            return URL.createObjectURL(blob);
        }
        return undefined;
    };

    handleButtonClick = () => {
        const { recordingState } = this.state;
        const showStartRecordButton = this.shouldShowStartRecordButton();

        if (showStartRecordButton) {
            this.startRecord();
        } else if (recordingState === STATE_RECORDING_STARTED) {
            this.stopRecord();
            if (marketplacePropertiesService.isRecordingDurationCounterEnabled()) {
                this.stopTimer();
            }
        }
    };

    autoStopRecording = () => {
        if (this.state.recordingState === STATE_RECORDING_STARTED) {
            this.stopRecord();
            if (marketplacePropertiesService.isRecordingDurationCounterEnabled()) {
                this.stopTimer();
            }
        }
    };

    hasExistingResponse = () => {
        const { userResponse, questionModel } = this.props;
        const contentRevisionId = questionModel?.contentRevisionId;
        const hasUserResponse = userResponse?.length > 0;
        const hasRecordedChunks = recordedChunks[contentRevisionId]?.length > 0;

        return !!(hasUserResponse || hasRecordedChunks);
    };

    isRecordingCapable = () => {
        return !!(navigator.mediaDevices?.getUserMedia &&
            typeof MediaRecorder !== 'undefined' &&
            MediaRecorder.isTypeSupported);
    };

    shouldAutoStartRecording = () => {
        const { context, isCurrent } = this.props;
        const { recordingState } = this.state;
        return this.isRecordingCapable() &&
            marketplacePropertiesService.isAutoAudioRecordingEnabled() &&
            context === EXAM_CONTEXT &&
            isCurrent &&
            !this.hasExistingResponse() &&
            !this.autoStartFailed &&
            !recordingState;
    };

    shouldShowStartRecordButton = () => {
        const { recordingState } = this.state;
        const { context } = this.props;
        return (context === EXAM_CONTEXT || context === PREVIEW_CONTEXT) &&
            !(recordingState === STATE_RECORDING_UPLOADING) &&
            (!recordingState || recordingState !== STATE_RECORDING_STARTED) &&
            !this.shouldAutoStartRecording();
    };

    shouldShowStopRecordButton = () => {
        const { context } = this.props;
        const autoRecordingActive = marketplacePropertiesService.isAutoAudioRecordingEnabled() &&
            context === EXAM_CONTEXT &&
            !this.hasExistingResponse();
        return !autoRecordingActive;
    };

    render() {
        const { recordingState, stream } = this.state;
        const isRecordingStarted = recordingState === STATE_RECORDING_STARTED;
        const { model, direction } = this.props;
        const showUploadInProgress = recordingState === STATE_RECORDING_UPLOADING;
        const showVolumeMeter = stream && isRecordingStarted;
        const showStartRecordButton = this.shouldShowStartRecordButton();
        const showStopRecordButton = isRecordingStarted && this.shouldShowStopRecordButton();

        const audioURL = !showVolumeMeter && !showUploadInProgress ? this.getAudioUrl() : undefined;

        let buttonStyle;
        if (showStartRecordButton) {
            buttonStyle = styles.recordButton;
        } else if (isRecordingStarted || this.shouldShowStopRecordButton()) {
            buttonStyle = styles.stopButton;
        }

        return (
            <div className="interaction" dir={direction}>
                <Prompt model={model.prompt} />

                { showVolumeMeter && <VolumeMeter stream={stream} /> }

                {/* eslint-disable-next-line jsx-a11y/media-has-caption */ }
                { audioURL && <audio src={audioURL} controls /> }

                { showUploadInProgress && <div className="fa fa-spinner fa-pulse fa-3x fa-fw" /> }
                { (showStartRecordButton || isRecordingStarted) &&
                    <div className={styles.recordButtonsWrapper}>
                        { (showStartRecordButton || showStopRecordButton) &&
                            <button className={buttonStyle} onClick={this.handleButtonClick}>
                                {
                                    showStartRecordButton &&
                                        <React.Fragment>
                                            <span className="sr-only">{t('interactions.audioRecord.record')}</span>
                                            <i className="fa fa-microphone" aria-hidden="true" />
                                        </React.Fragment>
                                }
                                {
                                    showStopRecordButton &&
                                        <React.Fragment>
                                            <span className="sr-only">{t('interactions.audioRecord.stop')}</span>
                                            <i className="fa fa-stop-circle-o" aria-hidden="true" />

                                        </React.Fragment>
                                }
                            </button>
                        }

                        { isRecordingStarted && this.state.recordTime?.length > 0 &&
                            <span className={styles.recordTime}>
                                { `${this.state.recordTime} ${t('interactions.audioRecord.recordTime')}` }
                            </span>
                        }
                    </div>
                }
            </div>
        );
    }
}

export default AudioRecordInteraction;
